import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, DevicePhoneMobileIcon, ShareIcon, PlusCircleIcon } from '@heroicons/react/24/outline';

/**
 * InstallPrompt Component
 * Shows a prompt to install the PWA on all devices
 * Uses bot.gif as the mascot image
 */
const InstallPrompt = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isIOS, setIsIOS] = useState(false);
    const [isAndroid, setIsAndroid] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        // Check if already installed (standalone mode)
        const checkStandalone = () => {
            const isInStandaloneMode =
                window.matchMedia('(display-mode: standalone)').matches ||
                window.navigator.standalone === true ||
                document.referrer.includes('android-app://');
            setIsStandalone(isInStandaloneMode);
            return isInStandaloneMode;
        };

        // Check if user has dismissed the prompt before
        const checkDismissed = () => {
            const dismissedTime = localStorage.getItem('pwa_install_dismissed');
            if (dismissedTime) {
                // Show again after 7 days
                const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
                if (daysSinceDismissed < 7) {
                    setDismissed(true);
                    return true;
                }
            }
            return false;
        };

        // Detect platform
        const userAgent = window.navigator.userAgent.toLowerCase();
        const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
        const isAndroidDevice = /android/.test(userAgent);

        setIsIOS(isIOSDevice);
        setIsAndroid(isAndroidDevice);

        // Don't show if already installed or recently dismissed
        if (checkStandalone() || checkDismissed()) {
            return;
        }

        // For iOS, show prompt after a delay (no beforeinstallprompt event)
        if (isIOSDevice) {
            const timer = setTimeout(() => {
                setShowPrompt(true);
            }, 5000); // Show after 5 seconds
            return () => clearTimeout(timer);
        }

        // For Android/Desktop, listen for beforeinstallprompt
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            // Show after a delay
            setTimeout(() => {
                setShowPrompt(true);
            }, 3000);
        };

        // Listen for the install prompt event
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Check for app installed event
        window.addEventListener('appinstalled', () => {
            setShowPrompt(false);
            setDeferredPrompt(null);
            localStorage.setItem('pwa_installed', 'true');
            localStorage.setItem('pwa_installed_at', Date.now().toString());
        });

        // For Mac/Desktop browsers that don't support beforeinstallprompt
        // Show a fallback prompt after a delay
        const isMacDesktop = /macintosh|mac os x/.test(userAgent) && !isIOSDevice;
        const isDesktop = !isIOSDevice && !isAndroidDevice;

        let fallbackTimer = null;
        if (isMacDesktop || isDesktop) {
            fallbackTimer = setTimeout(() => {
                setShowPrompt(true);
            }, 4000); // Show after 4 seconds on desktop
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            if (fallbackTimer) clearTimeout(fallbackTimer);
        };
    }, []);

    const handleInstall = async () => {
        if (deferredPrompt) {
            try {
                deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;
                if (outcome === 'accepted') {
                    setShowPrompt(false);
                    localStorage.setItem('pwa_installed', 'true');
                }
                setDeferredPrompt(null);
            } catch (error) {
                console.error('Error installing PWA:', error);
            }
        }
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        setDismissed(true);
        localStorage.setItem('pwa_install_dismissed', Date.now().toString());
    };

    const handleDismissForever = () => {
        setShowPrompt(false);
        setDismissed(true);
        localStorage.setItem('pwa_install_dismissed', (Date.now() + 365 * 24 * 60 * 60 * 1000).toString());
    };

    // Don't render if already installed, dismissed, or prompt not ready
    if (isStandalone || dismissed || !showPrompt) {
        return null;
    }

    return (
        <AnimatePresence>
            {showPrompt && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
                        onClick={handleDismiss}
                    />

                    {/* Prompt Modal */}
                    <motion.div
                        initial={{ opacity: 0, y: 100, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 100, scale: 0.9 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="fixed left-4 right-4 md:left-auto md:right-4 md:w-96
                       bg-white dark:bg-gray-900 rounded-2xl shadow-2xl z-[9999] overflow-hidden"
                        style={{ bottom: 'max(16px, env(safe-area-inset-bottom, 16px))' }}
                    >
                        {/* Close button */}
                        <button
                            onClick={handleDismiss}
                            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100 
                         transition-colors z-10"
                            aria-label="Close"
                        >
                            <XMarkIcon className="w-5 h-5 text-gray-400" />
                        </button>

                        {/* Content */}
                        <div className="p-6">
                            {/* Bot mascot image */}
                            <div className="flex justify-center mb-4">
                                <motion.img
                                    src="/bot.png"
                                    alt="SayDo Bot"
                                    className="w-24 h-24 object-contain"
                                    animate={{
                                        y: [0, -5, 0],
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: 'easeInOut',
                                    }}
                                />
                            </div>

                            {/* Title */}
                            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 text-center mb-2">
                                Install GK4I App
                            </h3>

                            {/* Description */}
                            <p className="text-gray-600 dark:text-gray-400 text-center text-sm mb-4">
                                Add GK4I to your home screen for quick access and a better experience!
                            </p>

                            {/* Platform-specific instructions */}
                            {isIOS ? (
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-4">
                                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-3 text-center">
                                        To install on iPhone/iPad:
                                    </p>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <ShareIcon className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                Tap the <strong>Share</strong> button in Safari
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <PlusCircleIcon className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                Select <strong>"Add to Home Screen"</strong>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : isAndroid && !deferredPrompt ? (
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-4">
                                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-3 text-center">
                                        To install on Android:
                                    </p>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <span className="text-green-600 font-bold">⋮</span>
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                Tap the <strong>menu button</strong> (⋮)
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <DevicePhoneMobileIcon className="w-4 h-4 text-green-600" />
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                Select <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : !isIOS && !isAndroid ? (
                                /* Desktop instructions */
                                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 mb-4">
                                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-3 text-center">
                                        To install on your computer:
                                    </p>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <span className="text-purple-600 font-bold text-xs">⊕</span>
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                Click the <strong>install icon</strong> in the URL bar (Chrome/Edge)
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <PlusCircleIcon className="w-4 h-4 text-purple-600" />
                                            </div>
                                            <span className="text-sm text-gray-600">
                                                Or use <strong>Menu → Install SayDo</strong>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {/* Install button (for supported browsers) */}
                            {deferredPrompt && (
                                <motion.button
                                    onClick={handleInstall}
                                    className="w-full py-3 px-4 bg-gradient-to-r from-indigo-500 to-purple-600 
                             text-white font-semibold rounded-xl shadow-lg hover:shadow-xl 
                             transition-all flex items-center justify-center gap-2"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <DevicePhoneMobileIcon className="w-5 h-5" />
                                    Install Now
                                </motion.button>
                            )}

                            {/* Dismiss options */}
                            <div className="flex justify-center gap-4 mt-4">
                                <button
                                    onClick={handleDismiss}
                                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                >
                                    Maybe later
                                </button>
                                <span className="text-gray-300">|</span>
                                <button
                                    onClick={handleDismissForever}
                                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                >
                                    Don't show again
                                </button>
                            </div>
                        </div>

                        {/* Bottom gradient accent */}
                        <div className="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default InstallPrompt;
